/**
 * ElementRegistry
 * Central registry for element type definitions
 */

import type { ElementDefinition, ElementSchema, ElementCategory } from './ElementSchema';

/**
 * Registration info stored in registry
 */
export interface ElementRegistration<TData = unknown, TAnswer = unknown> {
    type: string;
    definition: ElementDefinition<TData, TAnswer>;
    registeredAt: number;
}

/**
 * Element Registry - Singleton pattern
 */
class ElementRegistryClass {
    private elements: Map<string, ElementRegistration> = new Map();
    private listeners: Set<() => void> = new Set();

    /**
     * Register an element type
     */
    register<TData = unknown, TAnswer = unknown>(
        definition: ElementDefinition<TData, TAnswer>
    ): void {
        const { type } = definition.schema;

        if (this.elements.has(type)) {
            console.warn(
                `[ElementRegistry] Element type "${type}" is already registered. Overwriting.`
            );
        }

        this.elements.set(type, {
            type,
            definition: definition as ElementDefinition,
            registeredAt: Date.now(),
        });

        this.notifyListeners();
    }

    /**
     * Unregister an element type
     */
    unregister(type: string): boolean {
        const deleted = this.elements.delete(type);
        if (deleted) {
            this.notifyListeners();
        }
        return deleted;
    }

    /**
     * Get an element registration by type
     */
    get<TData = unknown, TAnswer = unknown>(
        type: string
    ): ElementRegistration<TData, TAnswer> | undefined {
        return this.elements.get(type) as ElementRegistration<TData, TAnswer> | undefined;
    }

    /**
     * Check if an element type is registered
     */
    has(type: string): boolean {
        return this.elements.has(type);
    }

    /**
     * Get all registered element types
     */
    getAll(): ElementRegistration[] {
        return Array.from(this.elements.values());
    }

    /**
     * Get element types by category
     */
    getByCategory(category: ElementCategory): ElementRegistration[] {
        return this.getAll().filter((reg) => reg.definition.schema.category === category);
    }

    /**
     * Get all registered type names
     */
    getTypes(): string[] {
        return Array.from(this.elements.keys());
    }

    /**
     * Get all element schemas
     */
    getSchemas(): ElementSchema[] {
        return this.getAll().map((reg) => reg.definition.schema);
    }

    /**
     * Clear all registrations
     */
    clear(): void {
        this.elements.clear();
        this.notifyListeners();
    }

    /**
     * Subscribe to registry changes
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Notify all listeners of changes
     */
    private notifyListeners(): void {
        this.listeners.forEach((listener) => listener());
    }

    /**
     * Get editor component for element type
     */
    getEditor(type: string): React.ComponentType<any> | undefined {
        return this.get(type)?.definition.EditComponent;
    }

    /**
     * Get player component for element type
     */
    getPlayer(type: string): React.ComponentType<any> | undefined {
        return this.get(type)?.definition.PlayerComponent;
    }

    /**
     * Get grader for element type
     */
    getGrader(type: string) {
        return this.get(type)?.definition.grader;
    }

    /**
     * Get validator for element type
     */
    getValidator(type: string) {
        return this.get(type)?.definition.validator;
    }

    /**
     * Get schema for element type
     */
    getSchema(type: string): ElementSchema | undefined {
        return this.get(type)?.definition.schema;
    }
}

// Singleton instance
export const ElementRegistry = new ElementRegistryClass();

// Export class for typing
export type { ElementRegistryClass };

/**
 * Helper function to register an element
 */
export function registerElement<TData = unknown, TAnswer = unknown>(
    definition: ElementDefinition<TData, TAnswer>
): void {
    ElementRegistry.register(definition);
}

/**
 * Helper function to create and register an element
 */
export function defineElement<TData = unknown, TAnswer = unknown>(
    definition: ElementDefinition<TData, TAnswer>
): ElementDefinition<TData, TAnswer> {
    ElementRegistry.register(definition);
    return definition;
}

/**
 * Hook to get element registration
 */
export function useElementRegistration<TData = unknown, TAnswer = unknown>(
    type: string
): ElementRegistration<TData, TAnswer> | undefined {
    const [registration, setRegistration] = React.useState(() =>
        ElementRegistry.get<TData, TAnswer>(type)
    );

    React.useEffect(() => {
        // Update on mount in case of SSR mismatch
        setRegistration(ElementRegistry.get<TData, TAnswer>(type));

        // Subscribe to registry changes
        return ElementRegistry.subscribe(() => {
            setRegistration(ElementRegistry.get<TData, TAnswer>(type));
        });
    }, [type]);

    return registration;
}

/**
 * Hook to get all registered elements
 */
export function useRegisteredElements(): ElementRegistration[] {
    const [elements, setElements] = React.useState(() => ElementRegistry.getAll());

    React.useEffect(() => {
        setElements(ElementRegistry.getAll());
        return ElementRegistry.subscribe(() => {
            setElements(ElementRegistry.getAll());
        });
    }, []);

    return elements;
}

/**
 * Hook to get elements by category
 */
export function useElementsByCategory(category: ElementCategory): ElementRegistration[] {
    const [elements, setElements] = React.useState(() => ElementRegistry.getByCategory(category));

    React.useEffect(() => {
        setElements(ElementRegistry.getByCategory(category));
        return ElementRegistry.subscribe(() => {
            setElements(ElementRegistry.getByCategory(category));
        });
    }, [category]);

    return elements;
}

// Import React for hooks
import * as React from 'react';
